// Safari reports VP9 support but drops the alpha channel, so it gets the opaque loop.
const SAFARI = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const ALPHA_TYPE = 'video/webm; codecs="vp09.00.41.08"';
const WEBM_TYPE = 'video/webm; codecs="vp9"';

const mobileQuery = matchMedia('(max-width: 749px)');
const reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');
// Gestures that count as user activation in WebKit, for a retry after the OS blocks autoplay.
const GESTURES = ['touchend', 'click', 'keydown'];

/**
 * Plays the Halloween hero video for the current breakpoint: the transparent WebM where the
 * browser can show its alpha, then the opaque WebM and MP4. The poster stays up until a frame
 * is ready, and stays for good if every file fails. The video and the icon loops pause while
 * the hero is off screen; with reduced motion the video holds its first frame. When the OS
 * refuses muted autoplay (iOS Low Power Mode), the next tap or key press starts it.
 */
class HalloweenHero extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {{ src: string, alpha: boolean }[]} */
  #queue = [];
  #index = 0;
  #visible = false;
  #loaded = false;
  #ghostFades = new Map();
  #wobbles = new Set();

  connectedCallback() {
    this.#video = this.querySelector('video');
    if (!this.#video) return;

    // iOS only plays without a gesture when muted and inline; set the properties as well as the
    // attributes before any source loads.
    this.#video.muted = true;
    this.#video.defaultMuted = true;
    this.#video.playsInline = true;

    this.addEventListener('click', this.#handleCharacter);
    this.#video.addEventListener('loadeddata', this.#handleFrame);
    this.#video.addEventListener('playing', this.#handlePlaying);
    this.#video.addEventListener('pause', this.#handlePause);
    this.#video.addEventListener('error', this.#handleError);
    mobileQuery.addEventListener('change', this.#handleBreakpoint);
    reducedMotionQuery.addEventListener('change', this.#sync);
    document.addEventListener('visibilitychange', this.#sync);
    window.addEventListener('pageshow', this.#sync);

    // A hidden hero (750px to 1000px) never intersects, so it never downloads a video.
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      if (this.#visible && !this.#loaded) this.#load();
      this.#sync();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleCharacter);
    for (const [ghost, fade] of this.#ghostFades) {
      fade.cancel();
      ghost.disabled = false;
    }
    this.#ghostFades.clear();
    for (const animation of this.#wobbles) animation.cancel();
    this.#wobbles.clear();
    this.#observer?.disconnect();
    this.#observer = null;
    mobileQuery.removeEventListener('change', this.#handleBreakpoint);
    reducedMotionQuery.removeEventListener('change', this.#sync);
    document.removeEventListener('visibilitychange', this.#sync);
    window.removeEventListener('pageshow', this.#sync);
    this.#listenForGesture(false);
    this.#visible = false;
    this.#loaded = false;
    this.#queue = [];
    this.classList.remove('is-ready', 'is-alpha', 'is-paused', 'is-blocked');

    const video = this.#video;
    if (!video) return;
    video.removeEventListener('loadeddata', this.#handleFrame);
    video.removeEventListener('playing', this.#handlePlaying);
    video.removeEventListener('pause', this.#handlePause);
    video.removeEventListener('error', this.#handleError);
    video.pause();
    video.removeAttribute('src');
    video.load();
  }

  #trackAnimation(animation, finish) {
    this.#wobbles.add(animation);
    animation.onfinish = () => {
      this.#wobbles.delete(animation);
      finish?.();
    };
    return animation;
  }

  #dissolveGhost(ghost) {
    if (ghost.disabled) return;
    ghost.disabled = true;
    const fadeDuration = reducedMotionQuery.matches ? 240 : 700;
    const duration = 3800 + fadeDuration;
    // One timeline keeps the original hidden until its return, without swapping animations.
    const fade = ghost.animate([
      { opacity: getComputedStyle(ghost).opacity, offset: 0, easing: 'ease-out' },
      { opacity: 0, offset: fadeDuration / duration },
      { opacity: 0, offset: 3800 / duration, easing: 'ease-out' },
      { opacity: 1, offset: 1 }
    ], { duration });
    this.#ghostFades.set(ghost, fade);
    fade.onfinish = () => {
      ghost.disabled = false;
      this.#ghostFades.delete(ghost);
    };
  }

  #handleCharacter = (event) => {
    const character = event.target.closest('.hw-it, .hw-candy-hit');
    if (!character || !this.contains(character)) return;
    if (character.hasAttribute('data-hw-ghost')) {
      this.#dissolveGhost(character);
      return;
    }
    if (!reducedMotionQuery.matches) {
      const targets = character.matches('.hw-candy-hit')
        ? this.querySelectorAll('.hw-hero__video, .hw-hero__poster img') : [character];
      for (const target of targets) {
        this.#trackAnimation(target.animate([
          { transform: 'rotate(0deg)', offset: 0 },
          { transform: 'rotate(-5deg)', offset: 0.3 },
          { transform: 'rotate(2deg)', offset: 0.68 },
          { transform: 'rotate(0deg)', offset: 1 }
        ], { duration: 900, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }));
      }
    }
  };

  #sources() {
    const viewport = mobileQuery.matches ? 'mobile' : 'desktop';
    const video = this.#video;
    const { [`${viewport}Alpha`]: alpha, [`${viewport}Webm`]: webm, [`${viewport}Mp4`]: mp4 } = this.dataset;
    const sources = [];

    if (alpha && !SAFARI && video?.canPlayType(ALPHA_TYPE)) sources.push({ src: alpha, alpha: true });
    if (webm && !SAFARI && video?.canPlayType(WEBM_TYPE)) sources.push({ src: webm, alpha: false });
    if (mp4) sources.push({ src: mp4, alpha: false });

    return sources;
  }

  #load() {
    this.#loaded = true;
    this.#queue = this.#sources();
    this.#index = -1;
    this.#next();
  }

  #next() {
    const video = this.#video;
    if (!video) return;

    this.classList.remove('is-ready', 'is-alpha');
    this.#index += 1;

    const source = this.#queue[this.#index];
    if (!source) {
      video.removeAttribute('src');
      video.load();
      return;
    }

    video.preload = 'auto';
    video.src = source.src;
    video.load();
    this.#sync();
  }

  #handleFrame = () => {
    this.classList.add('is-ready');
    this.#sync();
  };

  // Back icons go behind the candy only while the transparent video plays. A paused frame,
  // reduced motion, or blocked autoplay keeps them in front.
  #handlePlaying = () => {
    this.#listenForGesture(false);
    this.classList.add('is-ready');
    this.classList.remove('is-blocked');
    this.classList.toggle('is-alpha', Boolean(this.#queue[this.#index]?.alpha));
  };

  #handlePause = () => {
    this.classList.remove('is-alpha');
  };

  #handleError = () => {
    if (this.#video?.getAttribute('src')) this.#next();
  };

  // Swaps to the other breakpoint's files now if the hero shows, otherwise when it next intersects.
  #handleBreakpoint = () => {
    if (!this.#loaded) return;
    if (this.#visible && this.getClientRects().length) {
      this.#load();
      return;
    }

    this.#loaded = false;
    this.#queue = [];
    this.#next();
  };

  // Blocked autoplay (Low Power Mode, data saver) leaves the first frame or the poster up until
  // the visitor next taps or presses a key. Superseded loads reject with AbortError; ignore those.
  #handleRejection(error, src) {
    if (error?.name !== 'NotAllowedError' || !this.isConnected || this.#video?.getAttribute('src') !== src) return;
    this.classList.add('is-blocked');
    this.#listenForGesture(true);
  }

  #handleGesture = () => {
    this.#listenForGesture(false);
    this.#sync();
  };

  #listenForGesture(on) {
    for (const type of GESTURES) {
      if (on) document.addEventListener(type, this.#handleGesture, { capture: true, passive: true });
      else document.removeEventListener(type, this.#handleGesture, { capture: true });
    }
  }

  #sync = () => {
    const video = this.#video;
    if (!video) return;

    const play = this.#visible && !reducedMotionQuery.matches && document.visibilityState === 'visible';
    this.classList.toggle('is-paused', !this.#visible);
    video.autoplay = play;
    if (!video.getAttribute('src')) return;

    if (play) {
      const src = video.getAttribute('src');
      if (video.paused) video.play().catch((error) => this.#handleRejection(error, src));
      return;
    }

    video.pause();
    if (reducedMotionQuery.matches && video.currentTime > 0) video.currentTime = 0;
  };
}

if (!customElements.get('halloween-hero')) {
  customElements.define('halloween-hero', HalloweenHero);
}
